/**
 * What the user asked for on the New invoice screen, carried to the invoice
 * page that opens a moment later.
 *
 * Creating and sending are two calls against two routes, and between them the
 * user is navigated. Rather than run the send on the create page — which would
 * need its own copy of the send toasts, the "email is not configured" dialog
 * and the not-going-to-the-client banner — the create page records an intent in
 * the router's location state and the invoice page acts on it. One
 * implementation of sending, and the user watches it happen on the invoice.
 *
 * Location state and not a query parameter on purpose: it must not survive a
 * copied URL. `?intent=send` pasted into Slack would re-send an invoice for
 * whoever opened it.
 */

export type CreateIntent =
  /** Billable sends it from its own address and marks it sent. */
  | 'send'
  /** The user's own mail app sends it; Billable writes the message and logs it. */
  | 'manual';

export interface CreateIntentState {
  intent: CreateIntent;
}

/**
 * Read an intent back off `useLocation().state`, which is `unknown` and can be
 * anything a previous navigation put there — or a stale entry restored from
 * session history. Anything unrecognized is treated as no intent, because the
 * failure mode of guessing is an email nobody asked to send.
 */
export function readCreateIntent(state: unknown): CreateIntent | null {
  if (!state || typeof state !== 'object') return null;
  const intent = (state as { intent?: unknown }).intent;
  return intent === 'send' || intent === 'manual' ? intent : null;
}
